import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { AppState } from 'app/types';
import { Loggable } from 'app/utils/logging/loggable';
import CompanyController from 'rest/CompanyController';
import Paging from 'rest/Paging';
import SocialController from 'rest/SocialController';

import { VideoChatService } from '../../services/videochat.service';
import { SocialPageService } from '../../social-page/services/social-page.service';

import {
  EDIT_ROOM_REQ,
  EDIT_ROOM_ERR,
  SAVE_SOCIAL_SETTINGS_REQ,
  SAVE_SOCIAL_SETTINGS_ERR,
  SAVE_SOCIAL_SETTINGS_RSP,
} from './actions';

import { socialHomePageActions, socialHomePageSelectors } from '.';

const showLoaderActions = [EDIT_ROOM_REQ, SAVE_SOCIAL_SETTINGS_REQ];
const hideLoaderActions = [EDIT_ROOM_ERR, SAVE_SOCIAL_SETTINGS_ERR, SAVE_SOCIAL_SETTINGS_RSP];

@Injectable()
export class SocialHomePageEffects extends Loggable {
  private actions$ = inject(Actions);
  private videoChatService = inject(VideoChatService);
  private socialPageService = inject(SocialPageService);
  private store = inject<Store<AppState>>(Store);

  public constructor() {
    super();
    this.setDisplayName('SocialHomePageEffects');
  }

  public fetchRooms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.FETCH_SAVED_ROOMS_REQ),
      mergeMap(({ page, size }) =>
        from(new SocialController().getMySocials(false, new Paging(page, size, ['startDateTime,desc', 'name']))).pipe(
          map((rooms) => socialHomePageActions.FETCH_SAVED_ROOMS_RSP({ rooms })),
          tap({ error: (error: unknown) => this.error('fetchRooms$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.FETCH_SAVED_ROOMS_ERR({ error }))),
        ),
      ),
    );
  });

  public updateRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.EDIT_ROOM_REQ),
      mergeMap(({ open, room }) =>
        this.videoChatService.updateSocial(room).pipe(
          map((updatedRoom) => socialHomePageActions.EDIT_ROOM_RSP({ room: updatedRoom })),
          tap({
            next: async () => {
              if (open && room.socialId) {
                await this.socialPageService.onEnterRoom(room);
              }
            },
            error: (error: unknown) => {
              this.error('updateRoom$ error', error);
            },
          }),
          catchError((error: unknown) => of(socialHomePageActions.EDIT_ROOM_ERR({ error }))),
        ),
      ),
    );
  });

  public deleteDraftRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.DELETE_DRAFT_ROOM_REQ),
      mergeMap(({ room: { socialId } }) =>
        this.videoChatService.cancelSocial(socialId).pipe(
          map(() => socialHomePageActions.DELETE_DRAFT_ROOM_RSP({ roomId: socialId })),
          tap({ error: (error: unknown) => this.error('deleteDraftRoom$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.DELETE_DRAFT_ROOM_ERR({ error }))),
        ),
      ),
    );
  });

  public deleteRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.DELETE_ROOM_REQ),
      mergeMap(({ room: { socialId } }) =>
        this.videoChatService.cancelSocial(socialId).pipe(
          map(() => socialHomePageActions.DELETE_ROOM_RSP({ roomId: socialId })),
          tap({ error: (error: unknown) => this.error('deleteRoom$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.DELETE_ROOM_ERR({ error }))),
        ),
      ),
    );
  });

  public createRoom$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.CREATE_ROOM_REQ),
      mergeMap(({ room }) =>
        // XXX: Use update social here, and not create. We use a 'draft' social that's already persisted to the server
        // as the basis for the room. This is just updating the room with the new settings.
        from(new SocialController().updateSocial(room)).pipe(
          map((newRoom) => socialHomePageActions.CREATE_ROOM_RSP({ room: newRoom })),
          tap({ error: (error: unknown) => this.error('createRoom$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.CREATE_ROOM_ERR({ error }))),
        ),
      ),
    );
  });

  public getAlternateHostCandidate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_REQ),
      mergeMap(({ paging, socialId, text }) =>
        from(this.videoChatService.getAlternateHostCandidates({ socialId, paging, text })).pipe(
          map((alternateHostCandidates) =>
            socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_RSP({ alternateHostCandidates }),
          ),
          tap({ error: (error: unknown) => this.error('getAlternateHostCandidate$ error: ', error) }),
          catchError((error: unknown) => of(socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_ERR({ error }))),
        ),
      ),
    );
  });

  public getAlternateHostCandidateRoomSet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_ROOMSET_REQ),
      mergeMap(({ paging, socialRoomSetId, text }) =>
        from(this.videoChatService.getAlternateRoomSetHostCandidates({ socialRoomSetId, paging, text })).pipe(
          map((alternateHostCandidates) =>
            socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_RSP({ alternateHostCandidates }),
          ),
          tap({ error: (error: unknown) => this.error('getAlternateHostCandidate$ error: ', error) }),
          catchError((error: unknown) => of(socialHomePageActions.FETCH_ALTERNATE_HOSTS_CANDIDATES_ERR({ error }))),
        ),
      ),
    );
  });

  public saveAlternateHost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_REQ),
      mergeMap(({ alternateHosts, socialId }) =>
        from(this.videoChatService.updateAlternateHosts(socialId, alternateHosts)).pipe(
          map(() => socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_RSP()),
          tap({ error: (error: unknown) => this.error('saveAlternateHost$ error: ', error) }),
          catchError((error: unknown) => of(socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_ERR({ error }))),
        ),
      ),
    );
  });

  public saveAlternateHostRoomSet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_ROOMSET_REQ),
      mergeMap(({ alternateHosts, socialRoomSetId }) =>
        from(this.videoChatService.updateAlternateRoomSetHosts(socialRoomSetId, alternateHosts)).pipe(
          map(() => socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_RSP()),
          tap({ error: (error: unknown) => this.error('saveAlternateHost$ error: ', error) }),
          catchError((error: unknown) => of(socialHomePageActions.UPDATE__ALTERNATE_HOSTS_CANDIDATES_ERR({ error }))),
        ),
      ),
    );
  });

  public refetch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        socialHomePageActions.EDIT_ROOM_RSP,
        socialHomePageActions.DELETE_ROOM_RSP,
        socialHomePageActions.CREATE_ROOM_RSP,
      ),
      concatLatestFrom((_action) => this.store.select(socialHomePageSelectors.getSavedRooms)),
      switchMap(([_, { size }]) => [socialHomePageActions.FETCH_SAVED_ROOMS_REQ({ page: 0, size })]),
    );
  });

  public savePermissions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.SAVE_SOCIAL_SETTINGS_REQ),
      mergeMap(({ roomId, settings }) =>
        of(new SocialController().saveSocialSettings(roomId, settings)).pipe(
          map(() => socialHomePageActions.SAVE_SOCIAL_SETTINGS_RSP()),
          tap({ error: (error: unknown) => this.error('savePermissions$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.SAVE_SOCIAL_SETTINGS_ERR({ error }))),
        ),
      ),
    );
  });

  public saveRoomSet$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.SAVE_ROOMSET_SETTINGS_REQ),
      mergeMap(({ roomSetId, settings }) =>
        of(new SocialController().saveSocialRoomSetSettings(roomSetId, settings)).pipe(
          map(() => socialHomePageActions.SAVE_SOCIAL_SETTINGS_RSP()),
          tap({ error: (error: unknown) => this.error('savePermissions$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.SAVE_SOCIAL_SETTINGS_ERR({ error }))),
        ),
      ),
    );
  });

  public showLoader$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(...showLoaderActions),
      map(() => socialHomePageActions.SET_LOADING({ isLoading: true })),
    );
  });

  public hideLoader$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(...hideLoaderActions),
      map(() => socialHomePageActions.SET_LOADING({ isLoading: false })),
    );
  });

  public getUsersFromCompany$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(socialHomePageActions.GET_USERS_FROM_COMPANY_REQ),
      mergeMap(({ paging, text }) =>
        from(new CompanyController().findMyCompanyUsers(text, paging)).pipe(
          map((users) => socialHomePageActions.GET_USERS_FROM_COMPANY_RSP({ users })),
          tap({ error: (error: unknown) => this.error('getUsersFromCompany$ error', error) }),
          catchError((error: unknown) => of(socialHomePageActions.GET_USERS_FROM_COMPANY_ERR({ error }))),
        ),
      ),
    );
  });
}
